<template>
  <Transition name="banner">
    <div v-if="showBanner" class="onboarding-banner" role="alert">
      <div class="onboarding-banner__content">
        <div class="onboarding-banner__info">
          <Icon icon="mdi:rocket-launch-outline" class="onboarding-banner__icon" />
          <span class="onboarding-banner__message">
            You're <strong>{{ completedCount }}/{{ totalSteps }}</strong> of the way through setup
            &mdash; finish connecting your tools to get the most out of SalesBuckets.
          </span>
        </div>
        <div class="onboarding-banner__actions">
          <Button
            variant="secondary"
            size="xs"
            icon="mdi:arrow-right"
            icon-position="right"
            class="onboarding-banner__cta"
            @click="goToOnboarding"
          >
            Continue Setup
          </Button>
          <button class="onboarding-banner__dismiss" @click="dismissBanner" aria-label="Dismiss">
            <Icon icon="mdi:close" />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/Button.vue";
import { useOnboardingChecklist } from "@/composables/onboarding";
import { isOnboardingChecklistCompleted } from "@/utils/onboardingChecklistStorage";
import {
  isOnboardingBannerDismissedToday,
  isOnboardingBannerWindowExpired,
  dismissOnboardingBannerForToday,
  markOnboardingBannerFirstShown,
} from "@/utils/onboardingBannerStorage";
import { ONBOARDING_ROUTE } from "@/router/routes";
import { UserRoles } from "@/utils/enums";

defineOptions({
  name: "OnboardingBanner",
});

const store = useStore();
const router = useRouter();

const account = computed(() => store.getters.account?.data);
const userId = computed(() => account.value?.id || "");
const organizationId = computed(() => account.value?.organizationId || null);
const isOwner = computed(() => account.value?.role === UserRoles.owner);

const checklistParams = computed(() =>
  organizationId.value
    ? { organizationId: organizationId.value, userId: userId.value, isOwner: isOwner.value }
    : null
);
const { steps, completedCount, isComplete, isLoading } = useOnboardingChecklist(checklistParams);
const totalSteps = computed(() => steps.value.length);

const dismissedThisVisit = ref(false);

// Only starts the 7-day window once we've confirmed (post-load) that onboarding is
// genuinely incomplete for this user — avoids anchoring the window to a moment where the
// checklist data just hasn't finished loading yet.
watch(
  isLoading,
  (loading) => {
    if (
      !loading &&
      userId.value &&
      !isComplete.value &&
      !isOnboardingChecklistCompleted(userId.value)
    ) {
      markOnboardingBannerFirstShown(userId.value);
    }
  },
  { immediate: true }
);

const showBanner = computed(() => {
  if (isLoading.value || dismissedThisVisit.value || !userId.value) return false;
  if (isComplete.value || isOnboardingChecklistCompleted(userId.value)) return false;
  if (isOnboardingBannerWindowExpired(userId.value)) return false;
  if (isOnboardingBannerDismissedToday(userId.value)) return false;
  return true;
});

const dismissBanner = () => {
  dismissOnboardingBannerForToday(userId.value);
  dismissedThisVisit.value = true;
};

const goToOnboarding = () => {
  router.push({ name: ONBOARDING_ROUTE });
};
</script>

<style scoped>
.onboarding-banner {
  width: 100%;
  padding: 6px 24px;
  z-index: 1000;
  background: #3e3efe;
}

.onboarding-banner__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
}

.onboarding-banner__info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.onboarding-banner__icon {
  font-size: 18px;
  color: #fff;
  flex-shrink: 0;
}

.onboarding-banner__message {
  font-size: 0.8125rem;
  color: #fff;
}

.onboarding-banner__message strong {
  font-weight: 700;
}

.onboarding-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.onboarding-banner__cta {
  background: #fff !important;
  color: #3e3efe !important;
  border-color: #fff !important;
  font-size: 0.75rem !important;
  padding: 4px 10px !important;
  min-height: 28px !important;
  height: 28px !important;
}

.onboarding-banner__cta:hover {
  background: #f8f8ff !important;
}

.onboarding-banner__dismiss {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #fff;
  transition: background-color 0.2s ease;
}

.onboarding-banner__dismiss:hover {
  background: rgba(255, 255, 255, 0.25);
}

.banner-enter-active,
.banner-leave-active {
  transition: all 0.3s ease;
}

.banner-enter-from,
.banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

@media (max-width: 768px) {
  .onboarding-banner__content {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .onboarding-banner__actions {
    width: 100%;
    justify-content: space-between;
  }

  .onboarding-banner__cta {
    flex: 1;
    justify-content: center;
  }
}
</style>
